import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { userService } from "@/services";
import { formatEmail, isValidEmail } from "@/utils";
import { useUser, useLoading } from "@/contexts";
import { GoogleIcon, MicrosoftIcon } from "@/svgs";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { refreshUser } = useUser();
  const { withLoading } = useLoading();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const errors = useMemo(() => {
    const emailValue = formatEmail(formData.email);
    const emailError =
      emailValue.length === 0
        ? "Informe seu e-mail."
        : isValidEmail(emailValue)
          ? null
          : "E-mail inválido.";

    const passwordError =
      formData.password.length === 0 ? "Informe sua senha." : null;

    return { email: emailError, password: passwordError };
  }, [formData.email, formData.password]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "email" ? formatEmail(value) : value.replace(/^\s+|\s+$/g, ""),
    }));
    setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (errors.email || errors.password) return;

    setIsSubmitting(true);
    try {
      const result = await withLoading(() =>
        userService.login({
          email: formatEmail(formData.email),
          password: formData.password.trim(),
        }),
      );

      if (result.success) {
        await refreshUser();
        onClose();
        navigate("/inicio");
      } else {
        setSubmitError(result.error.detail ?? result.error.title);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsSubmitting(true);
    try {
      const result = await withLoading(() => userService.loginWithGoogle());
      if (result.success) {
        onClose();
        if (result.isNewUser) {
          navigate("/completar-cadastro", {
            state: {
              uid: result.user.uid,
              email: result.user.email,
              provider: "google",
            },
          });
        } else {
          await refreshUser();
          navigate("/inicio");
        }
      }
    } catch (error) {
      setSubmitError("Erro ao autenticar com o Google.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsSubmitting(true);
    try {
      const result = await withLoading(() => userService.loginWithMicrosoft());
      if (result.success) {
        onClose();
        if (result.isNewUser) {
          navigate("/completar-cadastro", {
            state: {
              uid: result.user.uid,
              email: result.user.email,
              provider: "microsoft",
            },
          });
        } else {
          await refreshUser();
          navigate("/inicio");
        }
      } else {
        setSubmitError(result.error.detail ?? result.error.title);
      }
    } catch (error) {
      setSubmitError("Erro ao autenticar com a Microsoft.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          &times;
        </button>

        <div className="auth-logo">
          <img
            src="/mimocupido-logo.png"
            alt="Mimo Cupido"
            className="auth-logo-icon"
          />
        </div>

        <div className="auth-social-wrapper">
          <button
            type="button"
            className="auth-social-btn google"
            onClick={handleGoogleLogin}
            disabled={isSubmitting}
          >
            <GoogleIcon />
            Continuar com o Google
          </button>

          <button
            type="button"
            className="auth-social-btn microsoft"
            onClick={handleMicrosoftLogin}
            disabled={isSubmitting}
          >
            <MicrosoftIcon />
            Continuar com a Microsoft
          </button>
        </div>

        <div className="auth-modal-separator">
          <span>ou</span>
        </div>

        <form className="auth-modal-form" onSubmit={handleSubmit}>
          <div className="auth-modal-input-group">
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="E-mail"
            />
          </div>

          <div className="auth-modal-input-group">
            <div className="auth-modal-password-wrapper">
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                placeholder="Senha"
              />
              <button
                type="button"
                className="auth-modal-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Ocultar" : "Mostrar"}
              </button>
            </div>
          </div>

          <button
            className="auth-modal-submit-btn"
            type="submit"
            disabled={
              isSubmitting || Boolean(errors.email) || Boolean(errors.password)
            }
          >
            {isSubmitting ? "Entrando..." : "Entrar"}
          </button>

          <div className="auth-modal-footer-links">
            <a
              href="/esqueci-senha"
              onClick={(e) => {
                e.preventDefault();
                navigate("/esqueci-senha");
                onClose();
              }}
            >
              Redefinir Senha
            </a>
          </div>

          <div className="auth-modal-footer-links">
            <span className="auth-modal-footer-text">Não tem conta?</span>

            <a
              href="/cadastro"
              onClick={(e) => {
                e.preventDefault();
                navigate("/cadastro");
                onClose();
              }}
            >
              Crie uma
            </a>
          </div>

          {submitError && <div className="auth-modal-error">{submitError}</div>}
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
